import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/ApiService';
import type { LimitedDesignationIssuance } from '../services/api/limitedDesignations';
import LimitedDesignationPlate from './limited-designation/LimitedDesignationPlate';
import { resolvePublicFileUrl } from '../utils/imageUrls';
import { formatLimitedDesignationOrdinal } from '../utils/limitedDesignationLabels';

const FoundingVehiclesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blurb, setBlurb] = useState<string | null>(null);
  const [editionSize, setEditionSize] = useState(50);
  const [issued, setIssued] = useState(0);
  const [items, setItems] = useState<LimitedDesignationIssuance[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getFoundingVehiclesCollection();
        if (cancelled) return;
        setBlurb(data.definition?.public_blurb || null);
        setEditionSize(data.definition?.edition_size || data.supply?.edition_size || 50);
        setIssued(data.supply?.issued || data.issuances.length);
        setItems(data.issuances || []);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load Founding Vehicles');
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:py-14">
      <p className="kicker mb-3 text-orange-300/80">Bilt Official</p>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
        Founding Vehicles
      </h1>
      <p className="mt-3 max-w-2xl text-gray-400 leading-relaxed">
        {blurb ||
          'Recognized among the first fifty vehicles that helped establish Bilt. Finite, intentional, and issued by Bilt.'}
      </p>
      <p className="mt-2 text-sm text-gray-500">
        {issued} of {editionSize} issued
      </p>

      {loading ? <p className="mt-10 text-sm text-gray-500">Loading…</p> : null}
      {error ? <p className="mt-10 text-sm text-red-400">{error}</p> : null}

      {!loading && !error && items.length === 0 ? (
        <p className="mt-10 text-sm text-gray-500">
          No Founding Vehicles are public yet. When issued and flags are on, they will appear here.
        </p>
      ) : null}

      <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((item) => {
          const href =
            item.vehicle?.username && item.vehicle?.public_slug
              ? `/${item.vehicle.username}/${item.vehicle.public_slug}`
              : null;
          const label =
            item.vehicle?.nickname ||
            `${item.vehicle?.year || ''} ${item.vehicle?.make || ''} ${item.vehicle?.model || ''}`.trim() ||
            item.title ||
            formatLimitedDesignationOrdinal(item.ordinal, item.edition_size);
          return (
            <li
              key={item.public_id}
              className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden flex flex-col"
            >
              {item.vehicle?.hero_image_url ? (
                <div className="aspect-[16/10] bg-black/40">
                  <img
                    src={resolvePublicFileUrl(item.vehicle.hero_image_url) || item.vehicle.hero_image_url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-[16/10] bg-gradient-to-br from-white/5 to-black/40" />
              )}
              <div className="p-4 flex flex-col gap-3 flex-1">
                <LimitedDesignationPlate designation={item} size="compact" />
                <p className="text-sm font-medium text-white">{label}</p>
                {href ? (
                  <Link to={href} className="text-sm text-orange-300/90 hover:underline mt-auto">
                    View build
                  </Link>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FoundingVehiclesPage;
